/**
 * Tek belge: ukba_kasa/kampanya_durumu — kampanya listesi, tablo satırları, şube görev işaretleri.
 * Firebase Console → Firestore → Kurallar: bu yola (ve genelde ukba_kasa) okuma/yazma izni verin; aksi halde
 * uygulama "permission-denied" veya toasta yansıtılan hata verir. Çok büyük base64 görseller Firestore
 * belge boyut sınırına (yakl. 1 MiB) takılabilir.
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export const ROWS_STORAGE_KEY = 'campaign_rows_v1';
export const STAFF_STATUS_STORAGE_KEY = 'campaign_staff_task_status_v1';
export const CAMPAIGN_LIST_STORAGE_KEY = 'campaign_list_v1';

export const getCampaignStateRef = () => doc(db, 'ukba_kasa', 'kampanya_durumu');

const safeString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const safeArray = (value) => (Array.isArray(value) ? value : []);

const stripLegacyCampaignImages = (c) => {
  if (!c || typeof c !== 'object') return c;
  const { aiImages, aiImagesStatus, ...rest } = c;
  return rest;
};

const migrateOneCampaign = (c) => {
  const r = stripLegacyCampaignImages(c);
  if (!r || typeof r !== 'object') return r;
  const {
    detailVisualPrompts: _a,
    detailVisualPromptsStatus: _b,
    ideaImagePrompts: _c,
    visualPrompts: _d,
    visualPromptsStatus: _e,
    ...rest
  } = r;
  return rest;
};

export const migrateCampaignListItems = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map(migrateOneCampaign);
};

export function readLocalCache() {
  let rows = [];
  let campaignList = [];
  let staffTaskStatus = {};
  try {
    const pRows = JSON.parse(localStorage.getItem(ROWS_STORAGE_KEY) || '[]');
    rows = Array.isArray(pRows) ? pRows : [];
  } catch (e) {
    rows = [];
  }
  try {
    const p = JSON.parse(localStorage.getItem(CAMPAIGN_LIST_STORAGE_KEY) || '[]');
    campaignList = Array.isArray(p) ? p.map(migrateOneCampaign) : [];
  } catch (e) {
    campaignList = [];
  }
  try {
    const p = JSON.parse(localStorage.getItem(STAFF_STATUS_STORAGE_KEY) || '{}');
    staffTaskStatus = p && typeof p === 'object' && !Array.isArray(p) ? p : {};
  } catch (e) {
    staffTaskStatus = {};
  }
  return { rows, campaignList, staffTaskStatus };
}

export function writeLocalCache({ rows, campaignList, staffTaskStatus }) {
  try {
    localStorage.setItem(ROWS_STORAGE_KEY, JSON.stringify(rows));
    localStorage.setItem(CAMPAIGN_LIST_STORAGE_KEY, JSON.stringify(campaignList));
    localStorage.setItem(STAFF_STATUS_STORAGE_KEY, JSON.stringify(staffTaskStatus));
  } catch (e) {
    // ignore
  }
}

export const bundleKey = (rows, campaignList, staffTaskStatus) => {
  try {
    return JSON.stringify({ rows, campaignList, staffTaskStatus });
  } catch (e) {
    return String(Date.now());
  }
};

export async function ensureCloudMigratedFromLocal() {
  const ref = getCampaignStateRef();
  const snap = await getDoc(ref);
  if (snap.exists()) return false;
  const { rows, campaignList, staffTaskStatus } = readLocalCache();
  if (!rows.length && !campaignList.length && !Object.keys(staffTaskStatus).length) {
    return false;
  }
  try {
    await setDoc(
      ref,
      {
        rows,
        campaignList: migrateCampaignListItems(campaignList),
        staffTaskStatus,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error('Kampanya verisi buluta taşınamadı', e);
    return false;
  }
}

export async function pushCampaignState({ rows, campaignList, staffTaskStatus }) {
  const ref = getCampaignStateRef();
  await setDoc(
    ref,
    {
      rows: safeArray(rows),
      campaignList: migrateCampaignListItems(safeArray(campaignList)),
      staffTaskStatus: staffTaskStatus && typeof staffTaskStatus === 'object' && !Array.isArray(staffTaskStatus) ? staffTaskStatus : {},
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
  writeLocalCache({ rows, campaignList, staffTaskStatus });
}

/**
 * Uygulamada aynı cihazda anlık cache + şube ekranı gibi noktalarla uyum.
 */
export async function patchCampaignState(partial) {
  const ref = getCampaignStateRef();
  const snap = await getDoc(ref);
  const prev = snap.exists() ? snap.data() : {};
  const rows = safeArray(partial.rows !== undefined ? partial.rows : prev.rows);
  const campaignList = migrateCampaignListItems(safeArray(partial.campaignList !== undefined ? partial.campaignList : prev.campaignList));
  const stIn = partial.staffTaskStatus !== undefined ? partial.staffTaskStatus : prev.staffTaskStatus;
  const staffTaskStatus = stIn && typeof stIn === 'object' && !Array.isArray(stIn) ? stIn : {};
  await setDoc(
    ref,
    {
      rows,
      campaignList,
      staffTaskStatus,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
  writeLocalCache({ rows, campaignList, staffTaskStatus });
}

export async function clearCampaignStateCloudAndLocal() {
  const ref = getCampaignStateRef();
  const empty = { rows: [], campaignList: [], staffTaskStatus: {} };
  await setDoc(
    ref,
    {
      ...empty,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
  try {
    localStorage.removeItem(ROWS_STORAGE_KEY);
    localStorage.removeItem(CAMPAIGN_LIST_STORAGE_KEY);
    localStorage.removeItem(STAFF_STATUS_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

export function getCampaignIdeaListUpdate(campaignList, campaignId, updatedCampaign) {
  const id = safeString(campaignId);
  const list = safeArray(campaignList);
  if (!id) return list;
  if (!list.length) {
    return list;
  }
  const has = list.some((item) => safeString(item?.id) === id);
  if (!has) {
    return list;
  }
  return list.map((item) => (safeString(item?.id) === id ? updatedCampaign : item));
}
